import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { style: {"display":"flex","flex-direction":"row","align-items":"center"} }

import { computed, Ref, ref } from "vue";

import BasePage from "@/components/BasePage.vue";
import SetupDeviceChecklist from "@/pages/setupDevicePage/components/setupDeviceChecklist.vue";
import {
  useConfigureParticipantId,
  useOpenBCIUtils,
  useWebsocketConnection,
} from "@/utils/hooks";
import { ConnectedDeviceStatus } from "@/utils/openBCISerialTypes";
import { PhArrowRight, PhWarningCircle } from "@phosphor-icons/vue";
import { useRoute } from "vue-router";
import { navigateToRestricted } from "@/router";
import { ROUTES } from "@/utils/routes";

// ---- STATE ----


export default /*@__PURE__*/_defineComponent({
  __name: 'SetupDevicePage',
  setup(__props) {

useConfigureParticipantId();
useWebsocketConnection();
const { setupSerialConnection } = useOpenBCIUtils();
const route = useRoute();

const isChecklistTurnOnHeadphonesChecked = ref(false);
const isChecklistConnectHeadphonesViaBluetoothChecked = ref(false);
const isChecklistDonglePluggedInChecked = ref(false);
const isCytonConnectionLoadingIndicatorShown = ref(false);
const isHeadSetConnected: Ref<boolean | ConnectedDeviceStatus> = ref(false);
const connectionErrorModal = ref({
  isModalOpen: false,
  title: "",
  message: "",
});

// ---- METHODS ----

const handleConnectSerialPort = async () => {
  isHeadSetConnected.value = await setupSerialConnection({
    setIsLoadingModalShown: (status: boolean) => {
      isCytonConnectionLoadingIndicatorShown.value = status;
    },
  });

  switch (isHeadSetConnected.value) {
    case ConnectedDeviceStatus.SUCCESS:
      connectionErrorModal.value = {
        isModalOpen: false,
        title: "",
        message: "",
      };

      handleRedirectToSignalCheck();
      break;
    case ConnectedDeviceStatus.NO_DATA_STREAMED:
      connectionErrorModal.value = {
        isModalOpen: true,
        title: "Wrong Port Selected",
        message:
          'No datastream detected. Please make sure to select the correct serial port in the browser pop up menu ("FT231X..." or "COM3").',
      };
      break;
    case ConnectedDeviceStatus.DONGLE_CONNECTED_BUT_HEADSET_NOT_FOUND:
      connectionErrorModal.value = {
        isModalOpen: true,
        title: "Headset not found",
        message:
          "Please make sure that the headset battery is charged and the headset is turned on.",
      };
      break;
    case false:
      connectionErrorModal.value = {
        isModalOpen: true,
        title: "Something went wrong.",
        message: "Please try again.",
      };
      break;
  }
};

const handleRetryHeadsetConnection = () => {
  connectionErrorModal.value.isModalOpen = false;
  handleConnectSerialPort();
};
const setIsChecklistTurnOnHeadphonesChecked = (value: boolean) => {
  isChecklistTurnOnHeadphonesChecked.value = value;
};

const setIsChecklistConnectHeadphonesViaBluetoothChecked = (value: boolean) => {
  isChecklistConnectHeadphonesViaBluetoothChecked.value = value;
};

const setIsChecklistDonglePluggedInChecked = (value: boolean) => {
  isChecklistDonglePluggedInChecked.value = value;
};

const handleRedirectToSignalCheck = () =>
  navigateToRestricted(ROUTES.OPTIMIZE_SIGNAL, route.query);

// ---- COMPUTED ----

const isChecklistCompleted = computed(
  () =>
    isChecklistTurnOnHeadphonesChecked.value &&
    isChecklistConnectHeadphonesViaBluetoothChecked.value &&
    isChecklistDonglePluggedInChecked.value,
);

return (_ctx: any,_cache: any) => {
  const _component_VBtn = _resolveComponent("VBtn")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BasePage, { heading: "Setup EEG Device" }, {
      default: _withCtx(() => [
        _cache[4] || (_cache[4] = _createElementVNode("p", {
          class: "mb-8",
          style: {"color":"#737373","text-align":"center","font-weight":"500","font-size":"18px"}
        }, [
          _createTextVNode(" We will now connect the EEG-Headset and check the signal quality. "),
          _createElementVNode("br"),
          _createTextVNode(" If you are ready, turn on your Headphones and plug in your USB Receiver. ")
        ], -1)),
        _createVNode(SetupDeviceChecklist, {
          isTurnOnHeadphonesChecked: isChecklistTurnOnHeadphonesChecked.value,
          isDonglePluggedInChecked: isChecklistDonglePluggedInChecked.value,
          isConnectHeadphonesViaBluetoothChecked: 
        isChecklistConnectHeadphonesViaBluetoothChecked.value
      ,
          setIsTurnOnHeadphonesChecked: setIsChecklistTurnOnHeadphonesChecked,
          setIsDonglePluggedInChecked: setIsChecklistDonglePluggedInChecked,
          setIsConnectHeadphonesViaBluetoothChecked: 
        setIsChecklistConnectHeadphonesViaBluetoothChecked
      
        }, null, 8, ["isTurnOnHeadphonesChecked", "isDonglePluggedInChecked", "isConnectHeadphonesViaBluetoothChecked", "setIsConnectHeadphonesViaBluetoothChecked"]),
        _createVNode(_component_VBtn, {
          disabled: !isChecklistCompleted.value,
          class: "mb-3",
          onClick: handleConnectSerialPort,
          "append-icon": _unref(PhArrowRight)
        }, {
          default: _withCtx(() => _cache[3] || (_cache[3] = [
            _createTextVNode(" Connect Headphones ")
          ])),
          _: 1
        }, 8, ["disabled", "append-icon"]),
        _cache[5] || (_cache[5] = _createElementVNode("p", { style: {"font-size":"13px","color":"#939393","text-align":"center","max-width":"600px"} }, [
          _createTextVNode(" Clicking the Button will prompt a browser pop-up, asking you to select which serial port to open. "),
          _createElementVNode("br"),
          _createTextVNode(" Select the option starting with either \"FT231X USB\" or \"COM3\". ")
        ], -1))
      ]),
      _: 1
    }),
    _createVNode(_component_v_dialog, {
      modelValue: isCytonConnectionLoadingIndicatorShown.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isCytonConnectionLoadingIndicatorShown).value = $event)),
      "max-width": "500px",
      persistent: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, { style: {"padding":"40px","border-radius":"12px","align-items":"center"} }, {
          default: _withCtx(() => [
            _createVNode(_component_v_progress_circular, {
              indeterminate: "",
              size: 41,
              width: 5,
              color: "#00594C",
              style: {"margin-bottom":"8px"}
            }),
            _createVNode(_component_v_card_text, { style: {"padding-bottom":"0"} }, {
              default: _withCtx(() => _cache[6] || (_cache[6] = [
                _createTextVNode(" Connecting Headset... ")
              ])),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: connectionErrorModal.value.isModalOpen,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((connectionErrorModal.value.isModalOpen) = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, { style: {"padding":"16px","border-radius":"12px"} }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(_unref(PhWarningCircle), {
                size: 24,
                color: "red"
              }),
              _createVNode(_component_v_card_title, {
                class: "pb-0 pl-2",
                style: {"text-align":"left"}
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(connectionErrorModal.value.title), 1)
                ]),
                _: 1
              })
            ]),
            _createVNode(_component_v_card_text, {
              class: "px-8 mb-1",
              style: {"text-align":"left"}
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(connectionErrorModal.value.message), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, {
              class: "pb-0",
              style: {"justify-content":"end"}
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_btn, {
                  onClick: _cache[1] || (_cache[1] = () => (connectionErrorModal.value.isModalOpen = false)),
                  rounded: "lg"
                }, {
                  default: _withCtx(() => _cache[7] || (_cache[7] = [
                    _createTextVNode("Close")
                  ])),
                  _: 1
                }),
                _createVNode(_component_VBtn, {
                  onClick: handleRetryHeadsetConnection,
                  rounded: "lg"
                }, {
                  default: _withCtx(() => _cache[8] || (_cache[8] = [
                    _createTextVNode("Retry")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})